import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { 
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell,
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { getModelColor } from '../utils/modelColors';
import { aggregateModelRecords, buildModelStackFromRows } from '../utils/modelAggregation';
import {
  MAX_ANALYTICS_RANGE_DAYS,
  applyDateRangeChange,
  getMaxEndDate,
  getQuickDateRange
} from '../utils/dateRange';
import "react-datepicker/dist/react-datepicker.css";

const AIModelDistribution = () => {
  const [loading, setLoading] = useState(true);
  const [requestDistribution, setRequestDistribution] = useState(null);
  const [teams, setTeams] = useState([]);
  const [dailyModelUsage, setDailyModelUsage] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(true);
  const [loadingModelUsage, setLoadingModelUsage] = useState(true);

  // Initialize dates - default to 14 days for optimal performance
  const [dateRange, setDateRange] = useState(() => ({
    startDate: startOfDay(subDays(new Date(), 14)),
    endDate: endOfDay(new Date())
  }));

  // Filter states
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [includeIT, setIncludeIT] = useState(false);

  // Chart height based on viewport
  const getChartHeight = () => {
    const width = window.innerWidth;
    if (width < 640) return 220; // Mobile
    if (width < 1024) return 280; // Tablet
    return 350; // Desktop
  };

  // Color palette for Request Types pie only (not AI models)
  const COLORS = ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F'];

  // Transform backend data into request types distribution for pie chart
  const getRequestTypesData = () => {
    if (!requestDistribution?.requestTypes) return [];
    
    const overview = requestDistribution.requestTypes;
    const requestTypes = [
      { name: 'Composer Requests', value: overview.totalComposerRequests || 0 },
      { name: 'Chat Requests', value: overview.totalChatRequests || 0 },
      { name: 'Agent Requests', value: overview.totalAgentRequests || 0 },
      { name: 'Cmd+K Usage', value: overview.totalCmdKUsages || 0 }
    ].filter(item => item.value > 0) // Only show types with usage
     .sort((a, b) => b.value - a.value); // Sort by usage descending
    
    return requestTypes;
  };

  // Aggregate AI models data for pie chart - show top 5 + Other
  const getAIModelsDistribution = () => {
    if (!requestDistribution?.topModels || !Array.isArray(requestDistribution.topModels)) return [];

    const allModels = aggregateModelRecords(requestDistribution.topModels, { modelKey: 'model' })
      .map((item) => ({
        name: item.model || 'Unknown',
        value: item.count || item.events || 0,
        percentage: 0
      }))
      .filter((item) => item.value > 0);

    if (allModels.length <= 5) {
      return allModels;
    }

    const top5 = allModels.slice(0, 5);
    const others = allModels.slice(5);
    const otherTotal = others.reduce((sum, item) => sum + item.value, 0);

    if (otherTotal > 0) {
      return [...top5, { name: 'Other', value: otherTotal, percentage: 0 }];
    }

    return top5;
  };

  // ----- Model Usage Trend helpers -----
  const ClampedTooltipContent = ({ active, payload, label, seriesOrder }) => {
    if (!active || !payload || payload.length === 0) return null;
    const items = Array.isArray(payload) ? [...payload] : [];
    if (Array.isArray(seriesOrder) && seriesOrder.length > 0) {
      const orderMap = new Map(seriesOrder.map((name, idx) => [name, idx]));
      items.sort((a, b) => {
        const ai = orderMap.has(a.name) ? orderMap.get(a.name) : Number.POSITIVE_INFINITY;
        const bi = orderMap.has(b.name) ? orderMap.get(b.name) : Number.POSITIVE_INFINITY;
        return bi - ai;
      });
    }
    return (
      <div style={{
        backgroundColor: '#fff',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        maxWidth: 360
      }}>
        <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{`Date: ${label}`}</p>
        {items.map((entry, index) => (
          <p key={`item-${index}`} style={{ margin: '2px 0', color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  };

  const ModelUsageTrendChart = ({ data, topModels = [] }) => {
    const containerRef = useRef(null);
    const [tooltipPos, setTooltipPos] = useState(null);

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48">
          <p className="text-secondary-500">No model usage data available</p>
        </div>
      );
    }

    let series = [];
    let formattedChartData = [];
    try {
      const stack = buildModelStackFromRows(
        data,
        topModels,
        4,
        kpis?.dateRange
      );
      series = stack.series;
      formattedChartData = stack.chartData.map((item) => {
        try {
          const dateOnly = typeof item.date === 'string' ? item.date.slice(0, 10) : item.date;
          const dateObj = typeof dateOnly === 'string' ? new Date(`${dateOnly}T12:00:00`) : dateOnly;
          return { ...item, formattedDate: format(dateObj, 'MMM dd') };
        } catch (e) {
          return { ...item, formattedDate: String(item.date).slice(0, 10) };
        }
      });
    } catch (e) {
      console.error('Error preparing ModelUsageTrend data:', e);
      return (
        <div className="flex items-center justify-center h-48">
          <p className="text-red-500">Error displaying model usage data</p>
        </div>
      );
    }

    const computeAndSetTooltipPos = (e) => {
      if (!containerRef.current || !e) return;
      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const approxWidth = 340;
      const lines = e.activePayload?.length || 1;
      const approxHeight = 32 + lines * 20 + 12;
      let x = (e.chartX || 0) + 12;
      let y = (e.chartY || 0) - approxHeight - 12;
      if (x + approxWidth > containerWidth) x = containerWidth - approxWidth - 8;
      if (x < 8) x = 8;
      if (y < 8) y = Math.min(containerHeight - approxHeight - 8, (e.chartY || 0) + 12);
      if (y < 8) y = 8;
      setTooltipPos({ x: Math.round(x), y: Math.round(y) });
    };

    return (
      <div ref={containerRef} style={{ width: '100%', height: getChartHeight() }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedChartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            onMouseMove={computeAndSetTooltipPos}
            onMouseLeave={() => setTooltipPos(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} tickLine={false} />
            <YAxis label={{ value: 'Usage Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} tick={{ fontSize: 12 }} />
            <Tooltip
              content={(props) => <ClampedTooltipContent {...props} seriesOrder={series} />}
              position={tooltipPos || undefined}
              wrapperStyle={{ pointerEvents: 'none' }}
              allowEscapeViewBox={{ x: true, y: true }}
              cursor={{ strokeDasharray: '3 3' }}
            />
            <Legend verticalAlign="bottom" height={36} iconType="circle" />
            {series.map((name, i) => (
              <Bar key={name} dataKey={name} stackId="models" fill={getModelColor(name, i)} name={name} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Load data function
  const loadAnalyticsData = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setLoadingDistribution(true);
    setLoadingModelUsage(true);
    
    try {
      const params = new URLSearchParams({
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        team: selectedTeam === 'all' ? '' : selectedTeam,
        includeIT: includeIT.toString()
      });

      // Fetch distribution data and model usage data in parallel
      const distributionPromise = api.get(`${endpoints.analytics.requestDistribution}?${params.toString()}`)
        .then(res => {
          setRequestDistribution(res.data);
          // Also fetch teams list if not loaded
          if (teams.length === 0) {
            api.get(endpoints.analytics.teams).then(res => setTeams(res.data.teams || []));
          }
        }).finally(() => setLoadingDistribution(false));

      const modelUsagePromise = api.get(`/analytics/daily-model-usage?${params.toString()}`)
        .then(res => setDailyModelUsage(res.data))
        .finally(() => setLoadingModelUsage(false));

      const kpisPromise = api.get('/analytics/kpis', { params: Object.fromEntries(params) })
        .then(res => setKpis(res.data));

      await Promise.all([distributionPromise, modelUsagePromise, kpisPromise]);

    } catch (error) {
      console.error('Failed to load analytics data:', error);
      toast.error('Failed to load analytics data');
      setLoadingDistribution(false);
      setLoadingModelUsage(false);
    } finally {
      setLoading(false); 
    }
  }, [selectedTeam, dateRange, includeIT, teams.length]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      loadAnalyticsData();
    }
  }, [loadAnalyticsData, dateRange]);

  const handleDateRangeChange = (field, date) => {
    const result = applyDateRangeChange(dateRange, field, date);
    if (!result.ok) {
      if (result.error) toast.error(result.error);
      return;
    }
    setDateRange(result.range);
  };

  const handleQuickDateRange = (days) => {
    setDateRange(getQuickDateRange(days));
    
    // Trigger data reload immediately for quick date selections
    setTimeout(() => loadAnalyticsData(), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 flex items-center gap-3">
            <BarChart3 className="text-primary-600" size={32} />
            AI Model Distribution
          </h1>
          <p className="mt-1 text-sm text-secondary-600">
            Analyze request types and AI model usage patterns
          </p>
        </div>
        <button
          onClick={loadAnalyticsData}
          className="btn btn-secondary flex items-center gap-2 self-start sm:self-center"
          disabled={loadingDistribution}
        >
          <RefreshCw className={`w-4 h-4 ${loadingDistribution ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Date Range
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) => handleDateRangeChange('startDate', date)}
                  selectsStart
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  maxDate={dateRange.endDate || endOfDay(new Date())}
                  dateFormat="MMM d, yyyy"
                  className="input w-full"
                  placeholderText="Start Date"
                />
                <span className="hidden sm:flex items-center text-secondary-500">to</span>
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) => handleDateRangeChange('endDate', date)}
                  selectsEnd
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  minDate={dateRange.startDate}
                  maxDate={getMaxEndDate(dateRange.startDate)}
                  dateFormat="MMM d, yyyy"
                  className="input w-full"
                  placeholderText="End Date"
                />
              </div>
            </div>

            {/* Quick Select */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Quick Select
              </label>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleQuickDateRange(7)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    7d
                  </button>
                  <button
                    onClick={() => handleQuickDateRange(14)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    14d
                  </button>
                  <button
                    onClick={() => handleQuickDateRange(30)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    30d
                  </button>
                </div>
                <p className="text-xs text-secondary-500">
                  Max {MAX_ANALYTICS_RANGE_DAYS} days per range. Selecting an earlier start date adjusts the end date automatically; you can shorten it.
                </p>
              </div>
            </div>

            {/* Team Filter */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Team
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="input w-full"
              >
                <option value="all">All Teams</option>
                {teams.map(team => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.teamName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Include IT Toggle */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="includeIT"
              checked={includeIT}
              onChange={(e) => setIncludeIT(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
            />
            <label htmlFor="includeIT" className="ml-2 block text-sm text-secondary-700">
              Include IT/Support Teams
            </label>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Types Distribution Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-secondary-900">
              Request Types Distribution
            </h3>
            <p className="text-sm text-secondary-600">
              Breakdown of Cursor AI request types by total usage count
            </p>
          </div>
          <div className="card-body">
            {loadingDistribution ? (
              <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={getChartHeight()}>
                <PieChart>
                  <Pie
                    data={getRequestTypesData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value, percent }) => 
                      `${name}: ${(percent * 100).toFixed(1)}%`
                    }
                    labelLine={false}
                  >
                    {getRequestTypesData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} requests`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* AI Models Used by Developers */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-secondary-900">
              AI Models Distribution
            </h3>
            <p className="text-sm text-secondary-600">
              Distribution of AI models by total API request count
            </p>
          </div>
          <div className="card-body">
            {loadingDistribution ? (
              <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={getChartHeight()}>
                {getAIModelsDistribution().length > 0 ? (
                  <PieChart>
                    <Pie
                      data={getAIModelsDistribution()}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value, percent }) => 
                        `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                      }
                      labelLine={false}
                    >
                      {getAIModelsDistribution().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getModelColor(entry.name, index)} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} requests`, name]}
                    />
                  </PieChart>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-secondary-500">No model usage data available</p>
                  </div>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Model Usage Trend (Daily) */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900">Model Usage Trend (Daily)</h3>
          <p className="text-sm text-secondary-600">Stacked by top models across the selected scope</p>
        </div>
        <div className="card-body">
          {loadingModelUsage ? (
            <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
              <LoadingSpinner />
            </div>
          ) : (
            <ModelUsageTrendChart 
              data={dailyModelUsage}
              topModels={(requestDistribution?.topModels || []).map((m) => m.model)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIModelDistribution;

